import React, { useState } from 'react';
import CascaDeModal from '../shared/CascaDeModal';
import { acaoPrincipal, acaoSecundaria } from '../../design';
import { TONE_BADGE, TONE_BORDER, TONE_SURFACE, TONE_TEXT } from '../../utils/statusColors';
import { fmtCompetencia, fmtCurrency } from '../../utils/formatters';
import {
  AVISOS_QUE_PEDEM_MOTIVO, AvisoDeEmpenho, PreviaDeEmpenhos, ROTULO_DO_AVISO, avisosForaDosBlocos, faixaDoEmpenho, fmtSaldo,
  fraseDasCompetencias,
} from '../../utils/empenho';

/**
 * A CONFIRMAÇÃO DA APROVAÇÃO COM EMPENHO (16/10/2026, fase 2, aprovada pelo Heitor). Só aparece quando o contrato está
 * DENTRO do controle (tem empenho cadastrado); fora dele, a aprovação segue como sempre. Um bloco por empenho, com o saldo
 * antes e depois (valor e competências), os itens sem empenho e os avisos. Nada bloqueia ("avisar, com motivo"): quando o
 * backend diz `exigeMotivo`, o botão só libera com o motivo escrito, que fica gravado na fatura.
 */
type Props = {
  codigo: string;
  previa: PreviaDeEmpenhos;
  titulo?: string;
  aviso?: React.ReactNode;
  enviando?: boolean;
  onCancelar: () => void;
  onConfirmar: (motivo: string) => void;
};

function Aviso({ a }: { a: AvisoDeEmpenho }) {
  const pede = AVISOS_QUE_PEDEM_MOTIVO.has(a.codigo);
  return (
    <div className={`rounded-lg border border-l-4 px-3 py-2 ${TONE_SURFACE.warning} ${TONE_BORDER.warning} border-l-amber-500`} data-aviso-de-empenho={a.codigo}>
      <p className={`text-sm font-semibold ${TONE_TEXT.warning}`}>
        ⚠ {ROTULO_DO_AVISO[a.codigo] || a.codigo}
        {pede && <span className={`ml-2 px-1.5 py-0.5 rounded text-[10px] ${TONE_BADGE.warning}`}>pede motivo</span>}
      </p>
      <p className='text-xs text-slate-300 mt-0.5'>{a.mensagem}</p>
    </div>
  );
}

function Saldo({ rotulo, valor, competencias }: { rotulo: string; valor: number; competencias: number }) {
  return (
    <div className='rounded-lg border border-central-borda bg-central-elevado px-3 py-2 min-w-0'>
      <p className='text-[11px] uppercase tracking-wide text-slate-400'>{rotulo}</p>
      <p className={`text-base font-semibold tabular-nums ${valor < 0 ? TONE_TEXT.danger : 'text-white'}`}>{fmtSaldo(valor)}</p>
      <p className='text-xs text-slate-400'>{fraseDasCompetencias(competencias)}</p>
    </div>
  );
}

export default function ConfirmacaoDeEmpenho({ codigo, previa, titulo, aviso, enviando, onCancelar, onConfirmar }: Props) {
  const [motivo, setMotivo] = useState('');
  const falta = previa.exigeMotivo && !motivo.trim();
  const foraDosBlocos = avisosForaDosBlocos(previa);
  return (
    <CascaDeModal
      largura='md'
      titulo={titulo || `Aprovar a fatura ${codigo}?`}
      subtitulo={`Competência ${fmtCompetencia(previa.competencia)} · valor da fatura ${fmtCurrency(previa.valorDaFatura)}`}
      onClose={onCancelar}
      temEdicaoNaoSalva={() => !!motivo.trim()}
      corpoClassName='px-5 py-4 space-y-3'
      rodape={
        <>
          <button type='button' onClick={onCancelar} className={`ml-auto px-4 py-2 rounded-lg text-sm ${acaoSecundaria}`}>Cancelar</button>
          <button type='button' onClick={() => onConfirmar(motivo.trim())} disabled={falta || enviando} data-aprovar-com-empenho
            title={falta ? 'Escreva o motivo para aprovar com este aviso' : undefined}
            className={`px-4 py-2 rounded-lg text-sm disabled:opacity-50 ${acaoPrincipal}`}>
            {enviando ? 'Aprovando…' : previa.exigeMotivo ? 'Aprovar com motivo' : 'Aprovar'}
          </button>
        </>
      }
    >
      {aviso}
      {previa.blocos.map((b) => (
        <section key={b.empenho.id} className='rounded-lg border border-central-borda p-3 space-y-2' data-bloco-de-empenho={b.empenho.number}>
          <div className='flex flex-wrap items-baseline justify-between gap-2'>
            <p className='text-sm text-white'>Empenho <b>{b.empenho.number}</b> <span className='text-slate-400'>· {faixaDoEmpenho(b.empenho)}</span></p>
            <p className='text-sm text-slate-300 tabular-nums'>nesta fatura: {fmtCurrency(b.valor)}</p>
          </div>
          {b.itens.length > 0 && (
            <p className='text-xs text-slate-400'>{b.itens.map((i) => i.name).filter(Boolean).join(' · ')}</p>
          )}
          <div className='grid grid-cols-[1fr_auto_1fr] items-center gap-2'>
            <Saldo rotulo='Saldo antes' {...b.saldoAntes} />
            <span className='text-slate-500' aria-hidden='true'>→</span>
            <Saldo rotulo='Saldo depois' {...b.saldoDepois} />
          </div>
          {b.avisos.map((a, i) => <Aviso key={i} a={a} />)}
        </section>
      ))}
      {previa.itensSemEmpenho.length > 0 && (
        <section className='rounded-lg border border-central-borda p-3' data-itens-sem-empenho>
          <p className='text-sm text-white mb-1'>Itens sem empenho</p>
          <ul className='text-xs text-slate-300 space-y-0.5'>
            {previa.itensSemEmpenho.map((i, k) => (
              <li key={k} className='flex justify-between gap-3'>
                <span>{i.name}{i.empenhoDeclarado ? <span className='text-slate-400'> · declara {i.empenhoDeclarado}, que não está cadastrado</span> : null}</span>
                <span className='tabular-nums'>{fmtCurrency(i.valor)}</span>
              </li>
            ))}
          </ul>
        </section>
      )}
      {foraDosBlocos.map((a, i) => <Aviso key={`f${i}`} a={a} />)}
      <p className='text-xs text-slate-400'>A fatura muda de Rascunho para Aberta. Nada é bloqueado: o aviso aceito fica gravado na fatura.</p>
      {previa.exigeMotivo && (
        <div>
          <label htmlFor='motivo-do-empenho' className='block text-xs text-slate-400 mb-1'>Motivo para aprovar assim (obrigatório; fica na fatura)</label>
          <textarea id='motivo-do-empenho' value={motivo} onChange={(e) => setMotivo(e.target.value)} rows={2} maxLength={500}
            className='w-full px-3 py-2 rounded-lg text-sm bg-central-elevado border border-amber-500/60 text-white placeholder:text-slate-500'
            placeholder='ex.: reforço do empenho já pedido ao órgão' />
        </div>
      )}
    </CascaDeModal>
  );
}
